import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Search, Star, Coins } from 'lucide-react'
import { api } from '../../lib/api'
import { formatCents } from '../../lib/money'
import { useServiceLines } from '../../lib/service-lines'
import Modal from '../ui/Modal'
import {
  RateKindShort,
  type RateKindT,
  type RatePickerItem,
  type RatePickerResponse,
} from '@jr2os/shared'

interface Props {
  open: boolean
  clientId: string | null
  /** Línea de servicio de la oportunidad asociada a la factura (filtra catálogo). */
  serviceLine?: string | null
  /** Tipos de tarifa que tiene sentido mostrar (defecto: todos). */
  allowedKinds?: ReadonlyArray<RateKindT>
  onClose: () => void
  onPick: (item: RatePickerItem) => void
}

/**
 * Picker modal con las tarifas aplicables a un cliente. Las tarifas asignadas
 * al cliente aparecen primero y marcadas con una estrella. Si el cliente tiene
 * override de importe, se muestra ya aplicado.
 */
export default function RatePickerModal({
  open,
  clientId,
  serviceLine,
  allowedKinds,
  onClose,
  onPick,
}: Props) {
  const { t } = useTranslation('rates')
  const { nameOf } = useServiceLines()
  const [items, setItems] = useState<RatePickerItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')

  useEffect(() => {
    if (!open) return
    if (!clientId) {
      setError(t('picker.errors.noClient'))
      setItems([])
      return
    }
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (serviceLine) params.set('serviceLine', serviceLine)
    api
      .get<RatePickerResponse>(`/rates/for-client/${clientId}?${params.toString()}`)
      .then((r) => {
        const filtered = allowedKinds
          ? r.items.filter((it) => allowedKinds.includes(it.kind))
          : r.items
        setItems(filtered)
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('picker.errors.loadFailed')))
      .finally(() => setLoading(false))
  }, [open, clientId, serviceLine, allowedKinds, t])

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase()
    if (!term) return items
    return items.filter(
      (it) =>
        it.name.toLowerCase().includes(term) ||
        (it.description?.toLowerCase().includes(term) ?? false),
    )
  }, [items, q])

  return (
    <Modal open={open} onClose={onClose} title={t('picker.title')} maxWidth="max-w-2xl">
      <div className="space-y-3">
        <div className="relative">
          <Search
            size={14}
            className="absolute left-2 top-1/2 -translate-y-1/2 t-faint pointer-events-none"
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('picker.searchPlaceholder')}
            className="w-full pl-7 pr-3 py-1.5 bg-[var(--bg-input)] border border-[var(--border)] rounded-lg text-sm t-primary placeholder:t-faint focus:outline-none focus:border-jr2-500/50"
          />
        </div>

        {error && (
          <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {loading ? (
          <div className="t-muted flex items-center gap-2 text-sm py-8 justify-center">
            <Loader2 size={14} className="animate-spin" /> {t('picker.loading')}
          </div>
        ) : filtered.length === 0 && !error ? (
          <div className="text-center text-sm t-muted py-8 flex flex-col items-center gap-2">
            <Coins size={20} className="t-faint" />
            {t('picker.empty')}
          </div>
        ) : (
          <ul className="max-h-[420px] overflow-y-auto divide-y divide-base/40 -mx-2">
            {filtered.map((it) => (
              <li key={it.rateId}>
                <button
                  type="button"
                  onClick={() => {
                    onPick(it)
                    onClose()
                  }}
                  className="w-full px-3 py-2.5 flex items-start gap-3 hover:bg-hover text-left"
                >
                  <div className="mt-0.5">
                    {it.isAssignedToClient ? (
                      <Star size={14} className="text-amber-700 dark:text-amber-300 fill-amber-300/40" />
                    ) : (
                      <span className="block w-3.5 h-3.5" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="t-primary font-medium truncate">{it.name}</div>
                    <div className="text-[11px] t-faint flex items-center gap-2 flex-wrap mt-0.5">
                      <span>{t('enums:RateKind.' + it.kind)}</span>
                      {it.serviceLine && <span>· {nameOf(it.serviceLine)}</span>}
                      {it.isAssignedToClient && (
                        <span className="text-amber-700 dark:text-amber-300">· {t('picker.assignedToClient')}</span>
                      )}
                      {it.description && (
                        <span className="t-muted truncate max-w-[280px]">· {it.description}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="t-primary font-mono text-sm">
                      {formatCents(it.appliedAmountCents)} {RateKindShort[it.kind]}
                    </div>
                    {it.appliedAmountCents !== it.baseAmountCents && (
                      <div className="text-[10px] t-faint">
                        {t('picker.base')} {formatCents(it.baseAmountCents)}
                      </div>
                    )}
                    <div className="text-[10px] t-faint">
                      {t('picker.vat')} {(it.vatRate * 100).toFixed(0)}%
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  )
}
